import { baseApi } from 'utils/apiUtil';
import {
  ChatError,
  SSEMessage,
  SSEConfig,
  SSEEventHandlers,
  EventType,
} from 'modules/chatbot/types/chatbot.types';

import {
  createChatError,
  getErrorCodeFromStatus,
  getErrorCodeFromFetchError,
} from 'modules/chatbot/utils/errorHandling';

// SSE endpoints
const SSE_ENDPOINTS = {
  STREAM: `${baseApi}/v2/assistant-relay/stream`,
} as const;

// Connection states
export enum SSEConnectionState {
  DISCONNECTED = 'disconnected',
  CONNECTING = 'connecting',
  CONNECTED = 'connected',
  RECONNECTING = 'reconnecting',
  ERROR = 'error',
}

// SSE service configuration
export interface SSEServiceConfig extends SSEConfig {
  enableAutoReconnect: boolean;
  timeout: number;
}

export const DEFAULT_SSE_CONFIG: SSEServiceConfig = {
  url: SSE_ENDPOINTS.STREAM,
  reconnectDelay: 1000,
  maxReconnectAttempts: 5,
  enableAutoReconnect: true,
  timeout: 60000,
};

// SSE Request payload interface
export interface SSERequestPayload {
  message: string;
  conversationId: string | null;
  history: Array<{
    role: 'user' | 'assistant';
    content: string;
  }>;
  llmContext: any;
}

class ChatbotSSEService {
  private config: SSEServiceConfig;
  private connectionState: SSEConnectionState = SSEConnectionState.DISCONNECTED;
  private reconnectAttempts = 0;
  private reconnectTimeout: NodeJS.Timeout | null = null;
  private eventHandlers: SSEEventHandlers = {};
  private connectionId: string | null = null;
  private abortController: AbortController | null = null;
  private currentEventType: string | null = null;
  private isIntentionalDisconnect: boolean = false;
  private accumulatedContent: string = '';
  private isAccumulatingChunk: boolean = false;

  constructor(config: Partial<SSEServiceConfig> = {}) {
    this.config = { ...DEFAULT_SSE_CONFIG, ...config };
    this.connectionId = this.generateConnectionId();
  }

  // Generate unique connection ID
  private generateConnectionId(): string {
    return `sse_${Date.now()}_${Math.random().toString(36).substring(2, 11)}`;
  }

  // Set event handlers
  setEventHandlers(handlers: SSEEventHandlers): void {
    this.eventHandlers = { ...this.eventHandlers, ...handlers };
  }

  // Simple state reset
  private resetStreamState(): void {
    this.currentEventType = null;
    this.accumulatedContent = '';
    this.isAccumulatingChunk = false;
  }

  // Send message via SSE using fetch with streaming
  async sendMessage(payload: SSERequestPayload): Promise<void> {
    if (this.connectionState === SSEConnectionState.CONNECTING) {
      throw createChatError(
        'SSE_CONNECTION_ERROR',
        'Connection already in progress',
        { connectionId: this.connectionId }
      );
    }

    // Reset stream state for new request
    this.resetStreamState();

    // Direct SSE request without retry wrapper
    return this.performSSERequest(payload);
  }

  private async performSSERequest(payload: SSERequestPayload): Promise<void> {
    try {
      this.setConnectionState(SSEConnectionState.CONNECTING);

      // Reset intentional disconnect flag for new requests
      this.isIntentionalDisconnect = false;

      // Create abort controller for this request
      this.abortController = new AbortController();

      const response = await fetch(this.config.url, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        credentials: 'include',
        body: JSON.stringify(payload),
        signal: this.abortController.signal,
      });

      if (!response.ok) {
        let errorMessage = `HTTP error! status: ${response.status}`;
        const errorCode = getErrorCodeFromStatus(response.status);

        try {
          const errorData = await response.json();
          if (errorData.code === 'validation.failed' && errorData.errors) {
            const validationErrors = errorData.errors
              .map(
                (err: any) =>
                  `${err.field}: ${
                    err.messageCodes?.[0] || 'validation failed'
                  }`
              )
              .join(', ');
            errorMessage = `Validation failed: ${validationErrors}`;
          } else if (errorData.message) {
            errorMessage = errorData.message;
          }
        } catch (parseError) {
          // Keep original error message if JSON parsing fails
        }

        throw createChatError(errorCode, errorMessage, {
          status: response.status,
          statusText: response.statusText,
          connectionId: this.connectionId,
        });
      }

      if (!response.body) {
        throw createChatError(
          'SSE_CONNECTION_ERROR',
          'No response body received',
          { connectionId: this.connectionId }
        );
      }

      this.setConnectionState(SSEConnectionState.CONNECTED);
      this.eventHandlers.onConnect?.();

      const reader = response.body.getReader();
      const decoder = new TextDecoder();
      let buffer = '';

      try {
        while (true) {
          const { done, value } = await reader.read();

          if (done) {
            // Send any remaining accumulated chunk content before completing
            if (this.isAccumulatingChunk && this.accumulatedContent) {
              this.sendAccumulatedChunk();
            }

            console.log('Stream completed');
            this.setConnectionState(SSEConnectionState.DISCONNECTED);
            this.eventHandlers.onComplete?.();
            break;
          }

          buffer += decoder.decode(value, { stream: true });
          const lines = buffer.split('\n');
          buffer = lines.pop() || '';

          for (const line of lines) {
            if (line.trim()) {
              try {
                this.processSSELine(line.trim());
              } catch (error) {
                const chatError =
                  error instanceof Error
                    ? createChatError('SSE_MALFORMED_MESSAGE', error.message, {
                        line: line.trim(),
                      })
                    : (error as ChatError);
                throw chatError;
              }
            }
          }
        }
      } finally {
        reader.releaseLock();
      }
    } catch (error) {
      // Ignore AbortErrors from intentional disconnects
      if (
        error instanceof Error &&
        error.name === 'AbortError' &&
        this.isIntentionalDisconnect
      ) {
        console.log('Ignoring AbortError from intentional disconnect');
        return;
      }

      let chatError: ChatError;
      if (error instanceof Error) {
        const errorCode = getErrorCodeFromFetchError(error);
        chatError = createChatError(errorCode, error.message, {
          connectionId: this.connectionId,
          originalError: error.name,
        });
      } else {
        chatError = error as ChatError;
      }

      this.handleError(chatError);
      this.setConnectionState(SSEConnectionState.ERROR);
      throw chatError;
    }
  }

  // Disconnect from SSE
  disconnect(): void {
    this.isIntentionalDisconnect = true;

    if (this.abortController) {
      this.abortController.abort();
      this.abortController = null;
    }

    if (this.reconnectTimeout) {
      clearTimeout(this.reconnectTimeout);
      this.reconnectTimeout = null;
    }

    // Reset stream state
    this.resetStreamState();

    this.setConnectionState(SSEConnectionState.DISCONNECTED);
    this.reconnectAttempts = 0;

    this.eventHandlers.onDisconnect?.();
  }

  // Get connection state
  getConnectionState(): SSEConnectionState {
    return this.connectionState;
  }

  // Get connection info
  getConnectionInfo() {
    return {
      state: this.connectionState,
      reconnectAttempts: this.reconnectAttempts,
      connectionId: this.connectionId,
      isConnected: this.connectionState === SSEConnectionState.CONNECTED,
    };
  }

  // Private methods
  private setConnectionState(state: SSEConnectionState): void {
    if (this.connectionState !== state) {
      this.connectionState = state;
      this.eventHandlers.onStateChange?.(state);
    }
  }

  private handleError(error: ChatError): void {
    this.eventHandlers.onError?.(error);
  }

  // Process individual SSE line
  private processSSELine(line: string): void {
    if (!line) return;

    if (line.startsWith('event:')) {
      // If we were accumulating chunk content, send it before starting new event
      if (this.isAccumulatingChunk && this.accumulatedContent) {
        this.sendAccumulatedChunk();
      }

      this.currentEventType = line.slice(6).trim();
      this.isAccumulatingChunk = this.currentEventType === 'chunk';
      this.accumulatedContent = '';
      return;
    }

    if (line.startsWith('data:')) {
      const content = line.slice(5).trim();

      if (this.currentEventType) {
        if (this.isAccumulatingChunk) {
          // For chunk events, accumulate content across multiple data lines
          if (this.accumulatedContent) {
            this.accumulatedContent += '\n' + content;
          } else {
            this.accumulatedContent = content;
          }
        } else {
          // For non-chunk events, process immediately
          const message: SSEMessage = {
            type: this.currentEventType as EventType,
            data: {
              type: this.currentEventType,
              content: content,
            },
            timestamp: Date.now(),
          };

          try {
            this.eventHandlers.onMessage?.(message);
          } catch (error) {
            const chatError =
              error instanceof Error
                ? createChatError('SSE_MALFORMED_MESSAGE', error.message, {
                    eventType: this.currentEventType,
                    content: content.substring(0, 100),
                    connectionId: this.connectionId,
                  })
                : (error as ChatError);
            throw chatError;
          }

          this.currentEventType = null;
        }
      }
      return;
    }

    // Empty line indicates end of event - send accumulated chunk if any
    if (line === '' && this.isAccumulatingChunk && this.accumulatedContent) {
      this.sendAccumulatedChunk();
    }
  }

  // Send accumulated chunk content
  private sendAccumulatedChunk(): void {
    if (!this.currentEventType || !this.accumulatedContent) return;

    const message: SSEMessage = {
      type: this.currentEventType as EventType,
      data: {
        type: this.currentEventType,
        content: this.accumulatedContent,
      },
      timestamp: Date.now(),
    };

    try {
      this.eventHandlers.onMessage?.(message);
    } catch (error) {
      const chatError =
        error instanceof Error
          ? createChatError('SSE_MALFORMED_MESSAGE', error.message, {
              eventType: this.currentEventType,
              content: this.accumulatedContent.substring(0, 100),
              connectionId: this.connectionId,
            })
          : (error as ChatError);
      throw chatError;
    }

    // Reset accumulation state
    this.currentEventType = null;
    this.accumulatedContent = '';
    this.isAccumulatingChunk = false;
  }

  // Update configuration
  updateConfig(newConfig: Partial<SSEServiceConfig>): void {
    this.config = { ...this.config, ...newConfig };
  }

  // Get current configuration
  getConfig(): SSEServiceConfig {
    return { ...this.config };
  }
}

// Export singleton instance
export const chatbotSSEService = new ChatbotSSEService();

// Export class for custom instances
export { ChatbotSSEService };
